import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

const Panel = ({ title, children }) => (
  <div className="bg-[#1E293B] border border-slate-700 rounded-xl p-4">
    <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-3">{title}</h3>
    {children}
  </div>
);
const inp = "bg-[#0F172A] border-slate-700 text-white h-9";

export const SecurityPanel = () => {
  const [logs, setLogs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [settings, setSettings] = useState({});
  const [pins, setPins] = useState({ current_pin: "", new_pin: "" });

  const load = useCallback(async () => {
    try {
      const [l, n, s] = await Promise.all([api.get("/admin/access-logs"), api.get("/admin/notifications"), api.get("/admin/settings")]);
      setLogs(l.data); setNotifs(n.data); setSettings(s.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <Panel title="Dati fatturazione (per il PDF)">
        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="settings-iban-input" className={inp} placeholder="IBAN" value={settings.iban || ""} onChange={(e) => setSettings({ ...settings, iban: e.target.value })} />
          <Input data-testid="settings-ragione-input" className={inp} placeholder="Ragione sociale" value={settings.ragione_sociale || ""} onChange={(e) => setSettings({ ...settings, ragione_sociale: e.target.value })} />
          <Input data-testid="settings-email-input" className={inp} placeholder="Email notifiche admin" value={settings.admin_email || ""} onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })} />
          <div className="flex gap-2 items-center">
            <label className="text-xs text-slate-400 whitespace-nowrap">Ore incluse evento</label>
            <Input data-testid="settings-durata-input" type="number" min="1" className={`${inp} w-20`} value={settings.durata_standard_ore ?? 4} onChange={(e) => setSettings({ ...settings, durata_standard_ore: Number(e.target.value) })} />
            <label className="text-xs text-slate-400 whitespace-nowrap">Extra €/ora</label>
            <Input data-testid="settings-tariffa-input" type="number" className={`${inp} w-24`} value={settings.tariffa_extra_ora ?? 50} onChange={(e) => setSettings({ ...settings, tariffa_extra_ora: Number(e.target.value) })} />
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <label className="text-xs text-slate-400 whitespace-nowrap">Esclusiva Interna €</label>
            <Input data-testid="settings-excl-interna" type="number" className={`${inp} w-24`} value={settings.prezzo_excl_interna ?? 500} onChange={(e) => setSettings({ ...settings, prezzo_excl_interna: Number(e.target.value) })} />
            <label className="text-xs text-slate-400 whitespace-nowrap">Esclusiva Dehors €</label>
            <Input data-testid="settings-excl-deorsi" type="number" className={`${inp} w-24`} value={settings.prezzo_excl_deorsi ?? 800} onChange={(e) => setSettings({ ...settings, prezzo_excl_deorsi: Number(e.target.value) })} />
            <label className="text-xs text-slate-400 whitespace-nowrap">Entrambe €</label>
            <Input data-testid="settings-excl-entrambe" type="number" className={`${inp} w-24`} value={settings.prezzo_excl_entrambe ?? 1300} onChange={(e) => setSettings({ ...settings, prezzo_excl_entrambe: Number(e.target.value) })} />
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <label className="text-xs text-slate-400 whitespace-nowrap">Capienza Interna</label>
            <Input data-testid="settings-cap-interna" type="number" className={`${inp} w-20`} value={settings.capienza_interna ?? 60} onChange={(e) => setSettings({ ...settings, capienza_interna: Number(e.target.value) })} />
            <label className="text-xs text-slate-400 whitespace-nowrap">Capienza Dehors</label>
            <Input data-testid="settings-cap-deorsi" type="number" className={`${inp} w-20`} value={settings.capienza_deorsi ?? 40} onChange={(e) => setSettings({ ...settings, capienza_deorsi: Number(e.target.value) })} />
          </div>
        </div>
        <Button data-testid="settings-save-button" className="bg-[#C85A32] hover:bg-[#a94a28] mt-3" onClick={async () => { await api.put("/admin/settings", settings); toast.success("Impostazioni salvate"); }}>Salva</Button>
      </Panel>

      <Panel title="Cambia PIN">
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="pin-current-input" className={`${inp} w-36`} type="password" maxLength={4} placeholder="PIN attuale" value={pins.current_pin} onChange={(e) => setPins({ ...pins, current_pin: e.target.value })} />
          <Input data-testid="pin-new-input" className={`${inp} w-36`} type="password" maxLength={4} placeholder="Nuovo PIN" value={pins.new_pin} onChange={(e) => setPins({ ...pins, new_pin: e.target.value })} />
          <Button data-testid="pin-change-button" className="bg-[#4A6B5D] hover:bg-[#3a5549]" onClick={async () => {
            try { await api.post("/auth/change-pin", pins); toast.success("PIN aggiornato"); setPins({ current_pin: "", new_pin: "" }); }
            catch (e) { toast.error(apiError(e)); }
          }}><KeyRound className="w-4 h-4 mr-1" /> Aggiorna PIN</Button>
        </div>
      </Panel>

      <Panel title="Log accessi (ultimi 100)">
        <div className="max-h-64 overflow-y-auto space-y-1">
          {logs.map((l, i) => (
            <div key={`${l.timestamp}-${i}`} data-testid={`access-log-row-${i}`} className="flex items-center gap-2 text-xs py-1 border-b border-slate-700/50">
              <ShieldCheck className={`w-3.5 h-3.5 ${l.success ? "text-emerald-400" : "text-rose-400"}`} />
              <span className="font-mono-num">{new Date(l.timestamp).toLocaleString("it-IT")}</span>
              <span className="text-slate-400">{l.action}</span>
              <span className="ml-auto text-slate-500">{l.ip}</span>
            </div>
          ))}
          {!logs.length && <p className="text-sm text-slate-500">Nessun accesso registrato</p>}
        </div>
      </Panel>

      <Panel title="Email inviate (MOCK — attivabili con chiave Resend)">
        <div className="max-h-48 overflow-y-auto space-y-1">
          {notifs.map((n, i) => (
            <div key={`${n.created_at}-${i}`} className="text-xs py-1.5 border-b border-slate-700/50">
              <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#F0B429]" /><b>{n.subject}</b></div>
              <div className="text-slate-400 mt-0.5">{n.body}</div>
            </div>
          ))}
          {!notifs.length && <p className="text-sm text-slate-500">Nessuna notifica</p>}
        </div>
      </Panel>
    </div>
  );
};
